// ShipForm.jsx
import React, { useState, useEffect } from 'react';
import MainEngineSubsection from './MainEngineSubsection';
import AuxiliaryEngineSubsection from './AuxiliaryEngineSubsection';
import FuelSubsection from './FuelSubsection';
import EmissionsSubsection from './EmissionsSubsection';
import EventsSubsection from './EventsSubsection';
import 'bootstrap/dist/css/bootstrap.min.css';
import { vesselTypes, countries } from './dropdownOptions';

export default function ShipForm({
  initialShipData = {},
  onSubmit,
  isEditMode = false,
}) {
  // Section toggles
  const [sectionsOpen, setSectionsOpen] = useState({
    general: true,
    technical: true,
    operations: true,
    events: true,
  });
  const toggleSection = (sec) =>
    setSectionsOpen((prev) => ({ ...prev, [sec]: !prev[sec] }));
  const arrow = (open) => (open ? '▼' : '►');

  // 1) Static fields
  const [shipData, setShipData] = useState({
    vesselName: initialShipData.vesselName || '',
    imoNumber: initialShipData.imoNumber || '',
    mmsi: initialShipData.mmsi || '', 
    callSign: initialShipData.callSign || '',
    vesselType: initialShipData.vesselType || '',
    email: initialShipData.email || '',
    dwt: initialShipData.dwt || '',
    dwtDesign: initialShipData.dwtDesign || '',
    eedi: initialShipData.eedi || '',
    builtYear: initialShipData.builtYear || '',
    flag: initialShipData.flag || '',
    loa: initialShipData.loa || '',
    lpp: initialShipData.lpp || '',
    displacement: initialShipData.displacement || '',
    breadth: initialShipData.breadth || '',
    grossTonnage: initialShipData.grossTonnage || '',
    netTonnage: initialShipData.netTonnage || '',
    cargoCapacity: initialShipData.cargoCapacity || '',
    operationalStatus: initialShipData.operationalStatus || '',
    crewSize: initialShipData.crewSize || '',
    areaOfOperation: initialShipData.areaOfOperation || '',
    lastVoyage: initialShipData.lastVoyage || '',
    hasTorqueMeter: initialShipData.hasTorqueMeter || false,
    hasKwhCounter: initialShipData.hasKwhCounter || false,
    manualPowerReporting:
      initialShipData.manualPowerReporting || false,
    ladenConsumption: initialShipData.ladenConsumption || '',
    ballastConsumption: initialShipData.ballastConsumption || '',
    hasIGGenerator: initialShipData.hasIGGenerator || false,
    hasScrubber: initialShipData.hasScrubber || false,
    dualFuel: initialShipData.dualFuel || false,
    blockCoefficient: initialShipData.blockCoefficient || '',
    frontAreaProjected: initialShipData.frontAreaProjected || '',
    anemometerHeight: initialShipData.anemometerHeight || '',
  });

  // 2) Dynamic subsections
  const [mainEngineData, setMainEngineData] = useState({
    numberOfMainEngines: initialShipData.numberOfMainEngines || 0,
    engines: initialShipData.mainEngines || [],
  });
  const [auxiliaryEngines, setAuxiliaryEngines] = useState(
    initialShipData.auxiliaryEngines || []
  );
  const [fuelTanks, setFuelTanks] = useState(
    initialShipData.fuelTanks || []
  );
  const [emissions, setEmissions] = useState(
    initialShipData.emissions || []
  );
  const [events, setEvents] = useState(initialShipData.events || []);

  // 3) Re-hydrate on prop change (for edit mode)
  useEffect(() => {
    setShipData((s) => ({
      ...s,
      vesselName: initialShipData.vesselName || '',
      imoNumber: initialShipData.imoNumber || '',
      mmsi: initialShipData.mmsi || '',
      callSign: initialShipData.callSign || '',
      vesselType: initialShipData.vesselType || '',
      email: initialShipData.email || '',
      dwt: initialShipData.dwt || '',
      dwtDesign: initialShipData.dwtDesign || '',
      eedi: initialShipData.eedi || '',
      builtYear: initialShipData.builtYear || '',
      flag: initialShipData.flag || '',
      loa: initialShipData.loa || '',
      lpp: initialShipData.lpp || '',
      displacement: initialShipData.displacement || '',
      breadth: initialShipData.breadth || '',
      grossTonnage: initialShipData.grossTonnage || '',
      netTonnage: initialShipData.netTonnage || '',
      cargoCapacity: initialShipData.cargoCapacity || '',
      operationalStatus: initialShipData.operationalStatus || '',
      crewSize: initialShipData.crewSize || '',
      areaOfOperation: initialShipData.areaOfOperation || '',
      lastVoyage: initialShipData.lastVoyage || '',
      hasTorqueMeter: initialShipData.hasTorqueMeter || false,
      hasKwhCounter: initialShipData.hasKwhCounter || false,
      manualPowerReporting:
        initialShipData.manualPowerReporting || false,
      ladenConsumption: initialShipData.ladenConsumption || '',
      ballastConsumption: initialShipData.ballastConsumption || '',
      hasIGGenerator: initialShipData.hasIGGenerator || false,
      hasScrubber: initialShipData.hasScrubber || false,
      dualFuel: initialShipData.dualFuel || false,
      blockCoefficient: initialShipData.blockCoefficient || '',
      frontAreaProjected:
        initialShipData.frontAreaProjected || '',
      anemometerHeight: initialShipData.anemometerHeight || '',
    }));
    setMainEngineData({
      numberOfMainEngines:
        initialShipData.numberOfMainEngines || 0,
      engines: initialShipData.mainEngines || [],
    });
    setAuxiliaryEngines(
      initialShipData.auxiliaryEngines || []
    );
    setFuelTanks(initialShipData.fuelTanks || []);
    setEmissions(initialShipData.emissions || []);
    setEvents(initialShipData.events || []);
  }, [initialShipData]);

  // 4) Handlers
  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setShipData((s) => ({
      ...s,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...shipData,
      numberOfMainEngines: mainEngineData.numberOfMainEngines,
      mainEngines: mainEngineData.engines,
      auxiliaryEngines,
      fuelTanks,
      emissions,
      events,
    });
  };

  // 5) Render
  return (
    <form onSubmit={handleSubmit}>
      {/* ── General ── */}
      <div className="mb-4">
      <div className="d-flex justify-content-between bg-light p-2 border">
          <h4 className="mb-0">General Information</h4>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => toggleSection('general')}
          >
            {arrow(sectionsOpen.general)}
          </button>
        </div>
        {sectionsOpen.general && (
          <div className="p-3 border border-top-0">
            {/* Row 1 */}
            <div className="row mb-3">
              {/* Vessel Name */}
              <div className="col-md-3">
                <label className="form-label">Vessel Name</label>
                <input
                  type="text"
                  name="vesselName"
                  className="form-control"
                  value={shipData.vesselName}
                  onChange={handleInputChange}
                />
              </div>

              {/* IMO Number */}
              <div className="col-md-3">
                <label className="form-label">IMO Number</label>
                <input
                  type="text"
                  name="imoNumber"
                  className="form-control"
                  value={shipData.imoNumber}
                  onChange={handleInputChange}
                />
              </div>

              {/* MMSI */}
              <div className="col-md-3">
                <label className="form-label">MMSI</label>
                <input
                  type="text"
                  name="mmsi"
                  className="form-control"
                  value={shipData.mmsi}
                  onChange={handleInputChange}
                />
              </div>

              {/* Call Sign */}
              <div className="col-md-3">
                <label className="form-label">Call Sign</label>
                <input
                  type="text"
                  name="callSign"
                  className="form-control"
                  value={shipData.callSign}
                  onChange={handleInputChange}
                />
              </div>

              {/* Vessel Type dropdown */}
              <div className="col-md-3">
                <label className="form-label">Vessel Type</label>
                <select
                  name="vesselType"
                  className="form-control"
                  value={shipData.vesselType}
                  onChange={handleInputChange}
                >
                  <option value="" disabled>
                    — Select Vessel Type —
                  </option>
                  {vesselTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Row 2 */}
            <div className="row mb-3">
              {[
                ['email', 'Email', 'email'],
                ['dwt', 'DWT', 'number'],
                ['dwtDesign', 'DWT-Design', 'number'],
                ['eedi', 'EEDI', 'number'],
              ].map(([key, label, type]) => (
                <div className="col-md-3" key={key}>
                  <label className="form-label">{label}</label>
                  <input
                    type={type}
                    name={key}
                    className="form-control"
                    value={shipData[key]}
                    onChange={handleInputChange}
                  />
                </div>
              ))}
            </div>
            {/* Row 3 */}
            <div className="row mb-3">
              {/* Built Year */}
              <div className="col-md-3">
                <label className="form-label">Built Year</label>
                <input
                  type="number"
                  name="builtYear"
                  className="form-control"
                  value={shipData.builtYear}
                  onChange={handleInputChange}
                />
              </div>

              {/* Flag dropdown */}
              <div className="col-md-3">
                <label className="form-label">Flag</label>
                <select
                  name="flag"
                  className="form-control"
                  value={shipData.flag}
                  onChange={handleInputChange}
                >
                  <option value="" disabled>
                    — Select Flag —
                  </option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* LOA */}
              <div className="col-md-3">
                <label className="form-label">LOA</label>
                <input
                  type="number"
                  name="loa"
                  className="form-control"
                  value={shipData.loa}
                  onChange={handleInputChange}
                />
              </div>

              {/* LPP */}
              <div className="col-md-3">
                <label className="form-label">LPP</label>
                <input
                  type="number"
                  name="lpp"
                  className="form-control"
                  value={shipData.lpp}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            {/* Row 4 */}
            <div className="row mb-3">
              {[
                ['displacement', 'Displacement', 'number'],
                ['breadth', 'Breadth', 'number'],
                ['grossTonnage', 'Gross Tonnage', 'number'],
                ['netTonnage', 'Net Tonnage', 'number'],
              ].map(([key, label]) => (
                <div className="col-md-3" key={key}>
                  <label className="form-label">{label}</label>
                  <input
                    type="number"
                    name={key}
                    className="form-control"
                    value={shipData[key]}
                    onChange={handleInputChange}
                  />
                </div>
              ))}
            </div>
            {/* Row 5 */}
            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label">
                  Cargo Capacity
                </label>
                <input
                  type="number"
                  name="cargoCapacity"
                  className="form-control"
                  value={shipData.cargoCapacity}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Technical ── */}
      <div className="mb-4">
        <div className="d-flex justify-content-between bg-light p-2 border">
          <h4 className="mb-0">Technical</h4>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => toggleSection('technical')}
          >
            {arrow(sectionsOpen.technical)}
          </button>
        </div>
        {sectionsOpen.technical && (
          <div className="p-3 border border-top-0">
            {/* Specifications */}
            <div className="mb-3">
              <h5 className="fw-bold">Specifications</h5>
              <div className="row mb-3">
                {[
                  ['blockCoefficient', 'Block Coefficient'],
                  ['frontAreaProjected', 'Front Area Projected'],
                  ['anemometerHeight', 'Anemometer Height'],
                ].map(([key, label]) => (
                  <div className="col-md-3" key={key}>
                    <label className="form-label">{label}</label>
                    <input
                      type="number"
                      name={key}
                      className="form-control"
                      value={shipData[key]}
                      onChange={handleInputChange}
                    />
                  </div>
                ))}
                <div className="col-md-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      name="hasTorqueMeter"
                      className="form-check-input"
                      checked={shipData.hasTorqueMeter}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label">
                      Has Torque Meter
                    </label>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      name="hasKwhCounter"
                      className="form-check-input"
                      checked={shipData.hasKwhCounter}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label">
                      Has kWh Counter
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      name="manualPowerReporting"
                      className="form-check-input"
                      checked={shipData.manualPowerReporting}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label">
                      Manual Power Reporting
                    </label>
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Laden Consumption
                  </label>
                  <input
                    type="number"
                    name="ladenConsumption"
                    className="form-control"
                    value={shipData.ladenConsumption}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Ballast Consumption
                  </label>
                  <input
                    type="number"
                    name="ballastConsumption"
                    className="form-control"
                    value={shipData.ballastConsumption}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="row mb-3">
                {[
                  ['hasIGGenerator', 'Has IG Generator'],
                  ['hasScrubber', 'Has Scrubber'],
                  ['dualFuel', 'Dual Fuel'],
                ].map(([key, label]) => (
                  <div className="col-md-4" key={key}>
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        name={key}
                        className="form-check-input"
                        checked={shipData[key]}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label">
                        {label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic subsections */}
            <MainEngineSubsection
              initialData={mainEngineData}
              onDataChange={setMainEngineData}
              reset={false}
              isEditMode={true}
            />
            <AuxiliaryEngineSubsection
              initialData={auxiliaryEngines}
              onDataChange={setAuxiliaryEngines}
              reset={false}
            />
            <FuelSubsection
              initialData={fuelTanks}
              onDataChange={setFuelTanks}
              reset={false}
            />
            <EmissionsSubsection
              initialData={emissions}
              onDataChange={setEmissions}
              reset={false}
            />
          </div>
        )}
      </div>

      {/* ── Operations ── */}
      <div className="mb-4">
        <div className="d-flex justify-content-between bg-light p-2 border">
          <h4 className="mb-0">Operations</h4>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => toggleSection('operations')}
          >
            {arrow(sectionsOpen.operations)}
          </button>
        </div>
        {sectionsOpen.operations && (
          <div className="p-3 border border-top-0">
            {/* No static fields here since EventsSubsection handles events */}
            <EventsSubsection
              initialData={events}
              onDataChange={setEvents}
              reset={false}
            />
          </div>
        )}
      </div>

      <button type="submit" className="btn btn-primary">
        {isEditMode ? 'Update Ship' : 'Save Ship'}
      </button>
    </form>
  );
}
